import { useMemo } from "react";
import { Box, Card, Chip, Grid, Typography } from "@material-ui/core";
import LoadingData from "components/Loading/LoadingData";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import {
  columnTextField,
  columnDateField,
  columnPriceField,
} from "utils/tables-utils";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import OrderDetailPanel from "./OrderDetailPanel";
import DocumentIconButton from "./DocumentIconButton";
import { formatStatus, isPrateleiraInfinita, isStatusPago } from "./utils";

function StatusChip({ status }) {
  const isPaid = isStatusPago(status);

  return (
    <Chip
      label={formatStatus(status)}
      size="small"
      style={{
        backgroundColor: isPaid
          ? themeColorsDesingSystem.green.background
          : themeColorsDesingSystem.orange.background,
        color: isPaid
          ? themeColorsDesingSystem.green.main
          : themeColorsDesingSystem.orange.main,
        fontWeight: 600,
        fontSize: "0.75rem",
      }}
    />
  );
}

function PrateleiraDocumentos({ row }) {
  const documentos = row?.detalhe?.documentos ?? {};

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      style={{ gap: 4 }}
    >
      <DocumentIconButton
        tooltip="Nota venda produto"
        disponivel={documentos.notaVendaProduto?.disponivel}
        tipo="nota"
        chaveNf={documentos.notaVendaProduto?.chaveNf}
        formatoArquivo={documentos.notaVendaProduto?.formatoArquivo}
        pdfNoS3={documentos.notaVendaProduto?.pdfNoS3}
        xmlNoS3={documentos.notaVendaProduto?.xmlNoS3}
        preferXml={false}
      />
      <DocumentIconButton
        tooltip="Boleto comissão"
        disponivel={documentos.boletoComissao?.disponivel}
        tipo="boleto"
        numeroTitulo={documentos.boletoComissao?.numeroTitulo}
      />
    </Box>
  );
}

export default function TableEncontroContas({
  data,
  totalRegistros,
  page,
  sizePerPage,
  onTableChange,
  searched,
  loading,
}) {
  const columnModel = [
    columnTextField({
      dataField: "marca",
      text: "Marca",
      align: "center",
    }),
    columnTextField({
      dataField: "canal",
      text: "Canal",
      align: "center",
    }),
    columnTextField({
      dataField: "numeroPedido",
      text: "Número do Pedido",
      align: "center",
    }),
    columnDateField({
      dataField: "dataPedido",
      text: "Data do Pedido",
      align: "center",
    }),
    columnPriceField(
      {
        dataField: "valorTotalPedido",
        text: "Valor Total do Pedido",
        align: "center",
      },
      false,
    ),
    columnPriceField(
      {
        dataField: "valorComissao",
        text: "Valor da Comissão",
        align: "center",
      },
      false,
    ),
    columnTextField({
      dataField: "status",
      text: "Status",
      align: "center",
      formatter: (_, row) => <StatusChip status={row.status} />,
    }),
    columnTextField({
      dataField: "documentos",
      text: "Documentos",
      align: "center",
      csvExport: false,
      formatter: (_, row) => {
        if (!isPrateleiraInfinita(row.canal)) {
          return null;
        }

        return <PrateleiraDocumentos row={row} />;
      },
    }),
  ];

  const expandRow = useMemo(
    () => ({
      renderer: (row) => (
        <Grid item xs={12}>
          <OrderDetailPanel pedido={row} />
        </Grid>
      ),
      onlyOneExpanding: true,
      showExpandColumn: true,
      expandHeaderColumnRenderer: () => <span />,
      expandColumnRenderer: ({ expanded }) =>
        expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />,
    }),
    [],
  );

  if (loading) {
    return (
      <Card style={{ width: "100%", padding: "1.5rem" }}>
        <Typography variant="h2" style={{ marginBottom: "1rem" }}>
          Encontro de Contas
        </Typography>
        <LoadingData searching label="Buscando pedidos..." hiddenIcon />
      </Card>
    );
  }

  return (
    <ToolkitTable
      dataTable={data}
      searched={searched}
      keyField="id"
      columnModel={columnModel}
      headerTitle="Encontro de Contas"
      notFoundTitle="Nenhum pedido encontrado para o período selecionado."
      pagination={{
        options: [10, 25, 50],
        totalSize: totalRegistros,
        page: page + 1,
        sizePerPage,
        showTotal: true,
      }}
      bootstrapTableProps={{
        remote: Boolean(onTableChange),
        onTableChange,
        expandRow,
      }}
    />
  );
}
